// Illustrative engagement walkthroughs — clearly labeled, no invented clients or results.
export const CASE_STUDIES = [
  {
    slug: "connected-systems",
    area: "Connected systems",
    service: "systems-integration",
    icon: "Network",
    title: "Connecting Systems That Never Talked",
    profile: "Illustrative scenario: a 60-person distribution business",
    summary:
      "What a systems-integration engagement looks like when orders, inventory, and accounting each live in their own disconnected tool.",
    situation: [
      "Orders arrive through an e-commerce platform, get re-typed into an inventory system, then re-typed again into accounting.",
      "Stock levels are only accurate on the day someone runs a manual reconciliation.",
      "Month-end close takes two weeks because three systems disagree on the numbers.",
      "One tenured employee is the only person who knows the workarounds that hold it together.",
    ],
    approach: [
      "Map the real data flow — every re-entry point, every spreadsheet bridge, every workaround.",
      "Quantify the cost: hours per week, error rates, and decisions delayed by stale data.",
      "Design an integration plan that names a single source of truth for each record type.",
      "Sequence the work so the business keeps running — highest-pain connection first.",
      "Document everything so the fix doesn't depend on any one person.",
    ],
    outcomes: [
      "Orders flow from storefront to inventory to accounting without re-entry.",
      "Stock levels and financials agree — the same numbers everywhere, every day.",
      "Month-end close measured in days, not weeks.",
      "New hires can follow documented processes instead of tribal knowledge.",
    ],
  },
  {
    slug: "cybersecurity-clarity",
    area: "Reduced risk",
    service: "cybersecurity-assessment",
    icon: "ShieldCheck",
    title: "From Cyber Anxiety to a Prioritized Plan",
    profile: "Illustrative scenario: a 35-person professional services firm",
    summary:
      "What a cybersecurity assessment looks like for a leadership team that worries about breaches but doesn't know where they actually stand.",
    situation: [
      "Leadership loses sleep over ransomware headlines but has no idea if current protections are adequate.",
      "The cyber insurance renewal questionnaire asks about controls nobody can confidently confirm.",
      "An outsourced IT provider says everything is 'covered' — but there's no way to verify it.",
      "Client contracts increasingly require security attestations the firm can't document.",
    ],
    approach: [
      "Review access, backups, devices, training, and incident readiness in plain business terms — no fear tactics.",
      "Verify what's actually configured versus what's assumed (including a real backup restore test).",
      "Map findings against insurance requirements and client contract obligations.",
      "Rank every gap by real-world likelihood and business impact, matched to the firm's size and budget.",
    ],
    outcomes: [
      "A one-page exposure summary leadership actually understands.",
      "A prioritized, right-sized protection plan — fundamentals first, no gold-plating.",
      "Insurance questionnaires answered with confidence and evidence.",
      "A tested, one-page incident response plan with named contacts.",
    ],
  },
  {
    slug: "technology-roadmap",
    area: "A clear roadmap",
    service: "it-strategy-roadmapping",
    icon: "Map",
    title: "Replacing Crisis-Driven IT with a 24-Month Roadmap",
    profile: "Illustrative scenario: a 120-person manufacturer",
    summary:
      "What an IT strategy engagement looks like when every technology decision has been reactive — made one emergency at a time.",
    situation: [
      "Technology spending is approved crisis-by-crisis with no annual plan or budget.",
      "An aging ERP limits growth, but nobody can articulate the case — or the cost — of replacing it.",
      "Leadership and the IT team have completely different views of what should happen next.",
      "Vendors drive the agenda because there's no internal roadmap to measure proposals against.",
    ],
    approach: [
      "Start with business goals — growth targets, margin pressure, capacity constraints — not tools.",
      "Inventory every system, contract, and cost to establish the true current state.",
      "Facilitate leadership alignment on which gaps are strategic and which are just annoying.",
      "Build a Now / Next / Later roadmap with owners, budget ranges, and decision points.",
      "Set a quarterly review cadence so the roadmap stays a living plan.",
    ],
    outcomes: [
      "A 24-month technology roadmap that traces every initiative to a business goal.",
      "A defensible budget leadership approved once — instead of twelve emergency approvals.",
      "Vendor proposals evaluated against the plan, not against the best sales pitch.",
      "Leadership and IT working from one shared picture of what's next.",
    ],
  },
  {
    slug: "reporting-decisions",
    area: "Better decisions",
    service: "data-reporting-bi",
    icon: "BarChart3",
    title: "From Spreadsheet Fog to a Shared Source of Truth",
    profile: "Illustrative scenario: a multi-location retail operation",
    summary:
      "What a data & reporting engagement looks like when every meeting starts with an argument about whose numbers are right.",
    situation: [
      "Weekly leadership meetings begin with 20 minutes of reconciling conflicting spreadsheets.",
      "A key analyst spends most of each week manually assembling reports that are stale on arrival.",
      "Store managers can't see their own performance without asking head office.",
      "Decisions are delayed — or made on gut feel — because trusted numbers arrive too late.",
    ],
    approach: [
      "Identify the 10-12 numbers leadership actually uses to run the business.",
      "Trace each number to its source system and fix the definitions everyone disagrees on.",
      "Design automated dashboards that refresh themselves — sized to the tools already owned.",
      "Roll out by audience: leadership first, then store managers, with training for each.",
    ],
    outcomes: [
      "One shared dashboard everyone trusts — meetings start with decisions, not reconciliation.",
      "The analyst's week redirected from assembling reports to analyzing them.",
      "Store managers self-serve their own performance daily.",
      "Faster, evidence-based decisions across every location.",
    ],
  },
];

export const getCaseStudy = (slug) => CASE_STUDIES.find((c) => c.slug === slug);
